import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import React, { useCallback, useEffect, useState } from "react";
import {
  BackHandler,
  FlatList,
  View,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from "react-native";
import SearchIcon from "@assets/svg/SearchIcon";
import {
  getOutForDeliveryDbDetails,
  markOutForDelivery,
  assignTouchpointBulk,
} from "@services/api";
import { useFocusEffect } from "@react-navigation/native";
import _, { debounce } from "lodash";
import color from "@constants/color";
import { format } from "date-fns";
import TagList from "./components/TagList";
import SummaryData from "./components/SummaryData";
import UserCard from "./components/UserCard";
import BottomButton from "./components/BottomButton";
import Filter from "./components/Filter";
import ConfirmationModal from "./components/ConfirmationModal";
import PolybagModal from "./components/PolybagModal";
import { cardDraftKey } from "./cardDraftKey";

const draftPayloadFromCard = (item) => ({
  user_id: item.user_id,
  user_name: item.user_name,
  user_address: item.user_address,
  user_phone: item.user_phone,
  user_lat: item.user_lat,
  user_lng: item.user_lng,
  route_id: item.route_id,
  route_name: item.route_name,
  order_ids: item.order_ids,
  is_ofd: item.is_ofd,
  status: item.status,
  bag_names: item.bag_names,
  bag_ids: item.bag_ids,
  show_no_bag_icon: item.show_no_bag_icon,
  address_id: item.address_id ?? "",
  plastic_bag_opted_in: item.plastic_bag_opted_in,
  // Same count the Polybag modal sums per card (see selectedPolybagRows
  // below) — sent so the backend can store what the leader was shown next
  // to its own computed count. Always present (mirrors plastic_bag_opted_in
  // above, which is never conditionally omitted); defaults to 0 for a
  // not-opted-in / bagless card, same as this file's own
  // `item?.polybag?.count || 0` normalization.
  polybag_count: Number.isFinite(item?.polybag?.count) ? item.polybag.count : 0,
});

const OutForDeliveryDetails = ({ navigation, route }) => {
  const [ofdData, setOfdData] = useState({});
  const [selectedRouteId, setSelectedRouteId] = useState("ALL");
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPolybagModal, setShowPolybagModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notMarkedCount, setNotMarkedCount] = useState(0);
  const [draftOfd, setDraftOfd] = useState({});
  const [unassignBusyKey, setUnassignBusyKey] = useState(null);
  const { db } = route.params;

  useFocusEffect(
    useCallback(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRouteId]),
  );
  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };
    const backHandlerSub = BackHandler.addEventListener(
      "hardwareBackPress",
      backHandler,
    );
    return () => {
      backHandlerSub.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);

  const fetchData = async ({ searchTerm = "", isSearch = true } = {}) => {
    try {
      setIsLoading(true);
      const { data } = await getOutForDeliveryDbDetails({
        db_id: db?.db_id,
        route_id: selectedRouteId,
        search_term: isSearch ? searchTerm || searchText : "",
      });
      setOfdData(data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    setIsRefreshing(false);
  };

  // Poly bags needed for the CURRENT selection — summed over the ticked rows
  // only. A route-wide total would announce bags for customers the CP never
  // selected, which is why the backend sends per-customer counts and no total.
  const selectedPolybagRows = (ofdData?.customersList || [])
    .filter((item) => Boolean(draftOfd[cardDraftKey(item)]))
    .filter((item) => (item?.polybag?.count || 0) > 0)
    .map((item) => ({
      key: cardDraftKey(item),
      userName: item.user_name,
      count: item.polybag.count,
    }));

  const totalPolybags = _.sumBy(selectedPolybagRows, "count");

  const onPressMarkOfdBtn = async () => {
    const allNotMarkOfdCount = ofdData?.customersList?.filter(
      (item) => item?.is_ofd === false,
    ).length;
    const allDraftOfdCount = Object.keys(draftOfd).length;
    if (allNotMarkOfdCount !== allDraftOfdCount) {
      setNotMarkedCount(allNotMarkOfdCount - allDraftOfdCount);
      setShowConfirmationModal(true);
    } else {
      onSelectionConfirmed();
    }
  };

  // Runs once the selection itself is settled — either it was complete, or the
  // CP confirmed the "without N customer" prompt. Poly bags are the last gate
  // before submit so the count is the final thing the CP reads.
  const onSelectionConfirmed = () => {
    setShowConfirmationModal(false);
    if (totalPolybags > 0) {
      setShowPolybagModal(true);
      return;
    }
    onPressMarkOfd();
  };

  const onPressMarkOfd = async () => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (ofdData?.ofdDate !== today) {
      Alert.alert(
        "Failed",
        "You can only mark out for delivery for today's date.",
      );
      return;
    }
    try {
      setShowConfirmationModal(false);
      setShowPolybagModal(false);
      setIsLoading(true);
      const data = Object.values(draftOfd);
      const payload = {
        ofd_date: ofdData?.ofdDate,
        customers_list: data,
      };
      await markOutForDelivery({ payload, db_id: db?.db_id });
      setDraftOfd({});
      fetchData();
    } catch (error) {
      // B-89-fail:1 — Alert only; no fetchData / no draft clear (avoids stale-tick vs list drift)
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Mark out for delivery failed. Please try again.";
      Alert.alert("Mark OFD failed", String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  const changeRoute = (routeId) => {
    setSelectedRouteId(routeId);
    setDraftOfd({});
  };
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };

  const onSearchInputChange = debounce((text) => {
    setSearchText(text);
    fetchData({ searchTerm: text, isSearch: text.length > 0 });
  }, 500);
  const onToggleUserSwitch = (userData) => {
    const key = cardDraftKey(userData);
    const draftOfdCopy = _.clone(draftOfd);
    if (draftOfdCopy[key]) {
      delete draftOfdCopy[key];
    } else {
      _.set(draftOfdCopy, key, draftPayloadFromCard(userData));
    }
    setDraftOfd(draftOfdCopy);
  };

  // B-14 Layer C — Unassign from OFD cards using BE hints (flag-ON only).
  const onPressUnassign = (item) => {
    if (!item?.enable_unassign || !item?.user_id) return;
    const isTrip = Boolean(item.is_ofd);
    // The copy is translated SERVER-side (citymall-services #28939 sends
    // unassign_confirm_title / unassign_confirm_body already resolved for THIS
    // card's kind, so the trip-vs-route branch below is only the fallback's).
    // This screen has no client-side i18n namespace at all, so the English
    // literals are the mandatory fallback for a backend that predates #28939
    // and for any field being absent — not a translation path.
    const title =
      item.unassign_confirm_title ||
      (isTrip ? "Unassign this trip?" : "Unassign from route?");
    const message =
      item.unassign_confirm_body ||
      (isTrip
        ? "Removes this trip from Out for Delivery but keeps the customer on the boy's route so pending orders can still be marked OFD."
        : "Removes the customer from this boy's route (pending work only — no OFD strip).");
    Alert.alert(title, message, [
      { text: item.unassign_cancel_text || "Cancel", style: "cancel" },
      {
        text: item.unassign_text || "Unassign",
        style: "destructive",
        onPress: () => runUnassign(item),
      },
    ]);
  };

  const runUnassign = async (item) => {
    const busyKey = cardDraftKey(item);
    try {
      setUnassignBusyKey(busyKey);
      const tp = {
        cx_id: item.user_id,
        // Scope the unassign to THIS boy's CP-DB. Without cp_db_id the
        // backend's strip filter (tp.cp_db_id ?? null) treats null as "no
        // filter" and unassigns the cx from every CP-DB under the CP —
        // mirrors AddBundleSheet.js's unassign tp shape.
        cp_db_id: db?.db_id,
        route_id: "UNASSIGN",
        unassign_strip_ofd: item.unassign_strip_ofd === true,
        unassign_keep_touchpoint: item.unassign_keep_touchpoint === true,
      };
      if (item.unassign_trip_id != null || item.trip_id != null) {
        tp.unassign_trip_id = item.unassign_trip_id ?? item.trip_id;
      }
      await assignTouchpointBulk({ type: "TODAY", tps: [tp] });
      const draftKey = cardDraftKey(item);
      if (draftOfd[draftKey]) {
        const next = _.clone(draftOfd);
        delete next[draftKey];
        setDraftOfd(next);
      }
      await fetchData();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Unassign failed. Please try again.";
      Alert.alert("Unassign failed", String(msg));
    } finally {
      setUnassignBusyKey(null);
    }
  };

  const onToggleAllSwitch = (selectAll) => {
    const draftOfdCopy = _.cloneDeep(draftOfd);
    if (selectAll) {
      ofdData?.customersList?.forEach((item) => {
        if (
          item?.is_ofd === false &&
          item?.status === "PACKED" &&
          item?.enable_ofd === true
        ) {
          _.set(draftOfdCopy, cardDraftKey(item), draftPayloadFromCard(item));
        }
      });
    } else {
      Object.keys(draftOfdCopy).forEach((key) => {
        delete draftOfdCopy[key];
      });
    }
    setDraftOfd(draftOfdCopy);
  };

  const isToggleOn = ({ userData }) =>
    Boolean(draftOfd[cardDraftKey(userData)]);

  const _headerData = [
    {
      key: "ROUTE_LIST",
    },
    {
      key: "SUMMARY",
    },
    {
      key: "FILTER",
    },
  ];

  const _searchHeaderData = [
    {
      key: "SPACER",
    },
  ];

  const renderItem = ({ item }) => {
    if (item.key === "ROUTE_LIST") {
      return (
        <TagList
          tags={ofdData?.tags || []}
          selectedRouteId={selectedRouteId}
          onPress={changeRoute}
        />
      );
    } else if (item.key === "SUMMARY") {
      return (
        <SummaryData
          summary={ofdData?.summary || {}}
          disabled_reason_submit={ofdData?.disabled_reason_submit}
        />
      );
    } else if (item.key === "FILTER") {
      return <Filter item={item} onToggleAllSwitch={onToggleAllSwitch} />;
    } else if (item.key === "SPACER") {
      return <View style={styles.listSpacer} />;
    } else {
      return (
        <UserCard
          item={item}
          onToggleUserSwitch={onToggleUserSwitch}
          isToggleOn={isToggleOn({ userData: item })}
          onPressUnassign={onPressUnassign}
          unassignBusy={unassignBusyKey === cardDraftKey(item)}
        />
      );
    }
  };

  const renderFooter = () => {
    return <View style={styles.listFooter} />;
  };

  return (
    <View style={styles.screen}>
      <HeaderWithSearch
        headertitle={db?.db_name || "Delivery Boy"}
        headerColor="#fff"
        hideShadow={true}
        navigation={navigation}
        route={route}
        searchPlaceholder="Search customer, address, number"
        isSearchEnabled={isSearchEnabled}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        searchIcon={<SearchIcon height={24} width={24} />}
        onSearchInputChange={onSearchInputChange}
        onPressCrossIcon={() => onSearchInputChange("")}
        onPressSearchIcon={() => setIsSearchEnabled(true)}
      />
      {isLoading ? (
        <ActivityIndicator
          size="large"
          color={color.primary}
          style={styles.loading}
        />
      ) : (
        <>
          <FlatList
            showsVerticalScrollIndicator={false}
            data={
              isSearchEnabled
                ? _searchHeaderData.concat(ofdData?.customersList || [])
                : _headerData.concat(ofdData?.customersList || [])
            }
            keyExtractor={(item) => cardDraftKey(item)}
            renderItem={renderItem}
            stickyHeaderIndices={[0]}
            ListFooterComponent={renderFooter}
            refreshing={isRefreshing}
            onRefresh={onRefresh}
          />
          <BottomButton
            title={`Mark Out for delivery ${
              Object.keys(draftOfd).length > 0
                ? `(${Object.keys(draftOfd).length})`
                : ""
            }`}
            disable={
              !(Object.keys(draftOfd).length > 0) || !ofdData?.enable_ofd_submit
            }
            onPress={onPressMarkOfdBtn}
            loading={isLoading}
          />
        </>
      )}
      <ConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        customerCount={notMarkedCount}
        onPressConfirm={onSelectionConfirmed}
      />
      <PolybagModal
        visible={showPolybagModal}
        totalBags={totalPolybags}
        rows={selectedPolybagRows}
        selectedCount={Object.keys(draftOfd).length}
        loading={isLoading}
        onClose={() => setShowPolybagModal(false)}
        onConfirm={onPressMarkOfd}
      />
    </View>
  );
};
export default OutForDeliveryDetails;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  listSpacer: {
    height: 10,
  },
  listFooter: {
    height: 80,
  },
  loading: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
});
